import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {NgForm} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {environment} from '../../../environments/environment';
import {Paths} from '../../utils/Paths';
import {BaseComponent} from "../BaseComponent";
import {LoginUser} from "../../models/auth/LoginUser";
import {DxFormComponent} from "devextreme-angular";
import {SpringRestService} from "@aiknow-srl/aik-ngx-http";
import {PostLoginCB} from "../../ope/user/PostLoginCB";
import {SpringLoginResponse} from "@aiknow-srl/aik-ngx-http/lib/spring-rest/classes/SpringLoginResponse";
import {UserOpeService} from "../../services/ope/userOpe.service";
import {EditorEnterKeyEvent} from "devextreme/ui/form";
import {AuthOpeService} from "../../services/ope/authOpe.service";
import {GrpcAuthorizationOPEService} from "../../services/ope/grpc/authorizationOPE.service";
import {ResetPasswordCB} from "../../ope/auth/ResetPasswordCB";
import {ForgotPasswordComponent} from "./forgot-password/forgot-password.component";
import {AuthStateService} from "../../services/auth/auth-state.service";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent extends BaseComponent implements OnInit {

  // *******************************
  //          LOGIN PARAMS
  /**
   * Material Toggle Position
   */
  readonly LABEL_POSITION = 'after';
  /**
   * Flag true if token has been checked
   */
  isCheckedToken = false;
  /**
   * field that contains the password inserted by partitionUser
   */
  baseFeHref = '';
  forgotPasswordPopupOpen = false;
  @ViewChild(DxFormComponent, {static: false}) form!: DxFormComponent;
  public dialog = inject(MatDialog);
  isLoggingIn: boolean = false;
  // *******************************
  protected loginUser = new LoginUser();
  protected be = inject(SpringRestService);
  /**
   * Reference to template login form
   */
  @ViewChild('loginForm') private loginForm?: NgForm;
  @ViewChild('forgotPassword') private forgotPassword?: ForgotPasswordComponent;
  private route = inject(Router);
  private theRoute = inject(ActivatedRoute);
  private userService = inject(UserOpeService);
  private authService = inject(AuthOpeService);
  private authorizationService = inject(GrpcAuthorizationOPEService);
  private authState = inject(AuthStateService);

  // *****************************************
  //     COMPONENT ONINIT
  // *****************************************

  /**
   * OnInit method
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.authService.setLanguage('it');

    this.baseFeHref = window.location.href;

    this.logger.debug('LoginComponent initialized');

    if (this.theRoute.snapshot.queryParamMap.get('showExpiredTokenError')) {
      this.toastService.openTranslatedErrorToastMessage('ERROR.TOKEN_EXPIRED');
    } else if (this.theRoute.snapshot.queryParamMap.has(this.appService.PASSWORD_RESET_ACTION)) {
      // Reset password component
      const resetPasswordToken = this.theRoute.snapshot.queryParamMap.get(this.appService.PASSWORD_RESET_ACTION);
      this.route.navigate([Paths.RESET_PASSWORD], {queryParams: {token: resetPasswordToken}});
      return;
    }

    if (environment.bypassLogin) {
      this.bypassLogin();
      this.route.navigateByUrl(Paths.HOME);
    } else {
      this.doPostLogin();
    }
  }

  async bypassLogin() {
    this.isCheckedToken = true;

    const cb = new PostLoginCB();
    cb.i.channel = this.appService.APP_CHANNEL;

    this.userService.postLogin(cb).then(
      () => {
        // STORING ALL POSTLOGIN PARAMETERS IN GLOBAL MODEL
        this.appService.storeParamsIntoGlobalModel(cb);
      });
  }

  async doPostLogin() {
    if (this.authState.isLoggedIn()) {
      try {

        const cb = new PostLoginCB();
        cb.i.channel = this.appService.APP_CHANNEL;
        this.userService.postLogin(cb).then(
          () => {

            // STORING ALL POSTLOGIN PARAMETERS IN GLOBAL MODEL
            this.appService.storeParamsIntoGlobalModel(cb);
            this.isCheckedToken = true;
            this.route.navigateByUrl(Paths.HOME).then(() => {
              // this.logger.log('User auto-logged with valid token');
            });
          }).catch(
          (err: any) => {
            // EXPIRED TOKEN ERROR: STAY IN LOGIN
            this.logger.error(err);
            this.isCheckedToken = true;
            this.authState.clear();
            this.route.navigateByUrl(Paths.LOGIN);
          })
      } catch (err: any) {
        // EXPIRED TOKEN ERROR: STAY IN LOGIN
        this.logger.error(err);
        this.isCheckedToken = true;
        this.authState.clear();
        this.route.navigateByUrl(Paths.LOGIN);
      }
    } else {
      this.isCheckedToken = true;
    }
  }

  // *******************************
  //         COMPONENT METHODS
  // *******************************

  async awaitLogin() {
    this.login();
  }

  /**
   * the method calls the login service with the partitionUser credentials
   */
  login() {

    this.logger.log(`User ${this.loginUser.username} presses login button`);
    this.toastService.closeToastMessage();
    const result = this.form.instance.validate();

    if (result.isValid) {
      this.isLoggingIn = true;
      this.authorizationService.stopImpersonation();
      this.authService.login(this.loginUser.username, this.loginUser.password, this.loginUser.rememberMeFlag).then(
        async (success) => {
          // If ok, try to call postLogin service
          try {
            const cb = new PostLoginCB();
            cb.i.channel = this.appService.APP_CHANNEL;
            await this.userService.postLogin(cb);
            this.appService.storeParamsIntoGlobalModel(cb);
            this.logger.log(`Login succeeded!`);
            this.route.navigateByUrl(Paths.HOME).then(() => this.isLoggingIn = false);

          } catch (err: any) {
            this.authState.clear();
          }
        }, (error: SpringLoginResponse) => {
          this.clearLogin();
        }
      ).catch(() => this.clearLogin());
    } else {
      // INVALID FORM, VALIDATION HANDLED IN HTML
      this.toastService.openTranslatedErrorToastMessage('ERROR.CREDENTIALS_ERROR');
    }
  }

  clearLogin() {
    this.loginUser.password = '';
    this.isLoggingIn = false
  }

  changePasswordMode = (name: string) => {
    let editor = this.form.instance.getEditor(name);
    if (!!editor) {
      editor.option(
        'mode',
        editor.option('mode') === 'text' ? 'password' : 'text',
      );
    }
  };

  passwordEditorOptions = {
    mode: 'password',
    valueChangeEvent: 'keyup',
    buttons: [
      {
        name: 'password',
        location: 'after',
        options: {
          icon: 'eyeopen',
          stylingMode: 'text',
          onClick: () => this.changePasswordMode('password'),
        },
      },
    ],
  };

  /**
   * Print BE environment in home card
   */
  printEnvironments(): string {
    return environment.typeOfEnv;
  }

  editorEnterKey($event: EditorEnterKeyEvent) {

    if ($event.dataField === 'username') {
      let editor = this.form.instance.getEditor('password');
      if (!!editor) {
        editor.focus();
      }
    } else if ($event.dataField === 'password') {
      this.awaitLogin();
    }
  }

  resetPasswordStart() {
    this.forgotPasswordPopupOpen = true;
  }

  doResetPasswordRequest(email: string) {

    if (email !== null) {
      const cb = new ResetPasswordCB();
      cb.i.email = email;
      cb.i.baseFeUrl = this.gmService.composeBaseFEUrl();
      cb.i.action = this.appService.PASSWORD_RESET_ACTION;

      this.authService.resetPassword(cb).then((_) => {
        this.toastService.openTranslatedSuccessToastMessage('RESET_PASSWORD.REQUEST_OK');
        this.forgotPasswordPopupOpen = false;
      }).catch(_ => {
        if (!!this.forgotPassword) {
          this.forgotPassword.createRequestSent = false;
        }
      });
    } else {
      this.forgotPasswordPopupOpen = false;
    }


  }
}
